import { motion } from "framer-motion";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { LOGO_DIAMOND, lineReveal } from "@/lib/site";
import ContactCTA from "@/components/site/ContactCTA";

// Outcome-proving hero: a doctor with a patient (new patients = the promise).
const HERO_IMG =
  "https://images.unsplash.com/photo-1777331903190-341a3dd0441b?crop=entropy&cs=srgb&fm=jpg&w=1400&q=62";

const HEADLINE = ["10 NOVIH", "PACIJENATA", "U 30 DANA"];

// Objections for a retargeting audience (already familiar with us).
const OBJECTIONS = [
  {
    worry: "„Zvuči predobro da bi bilo istinito.”",
    answer:
      "Zato je garancija jasna: 10 pacijenata u 30 dana ili vraćamo cijeli ulog. Rizik je na nama, ne na vama.",
  },
  {
    worry: "„Već sam probao oglase bez rezultata.”",
    answer:
      "Radimo isključivo Meta oglase za klinike, sa sistemima i redovnim provjerama kvalitete. Upravo zato možemo garantirati ishod.",
  },
  {
    worry: "„Nemam vremena za još jednu agenciju.”",
    answer:
      "Sve vodimo mi — od strategije do optimizacije. Vaš jedini posao je primati nove pacijente.",
  },
];

const PROOF = [
  { klinika: "Implantološki Centar Smile", grad: "Bjelovar", metric: "1.000+ upita · 100.000 €+ prihoda" },
  { klinika: "Ordinacija Nela Sabo Kopić", grad: "Zagreb", metric: "CPL 1,12 € · 300+ HZZO predbilježbi" },
  { klinika: "Optika Lapić", grad: "Sinj", metric: "640.000 pregleda · uz 450 € budžeta" },
];

function LandingHeader() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-4 md:px-10">
        <a href="#top" data-testid="ponuda1-logo" className="flex items-center gap-3">
          <img src={LOGO_DIAMOND} alt="Onarf Marketing" className="h-8 w-8 object-contain" />
          <span className="font-display text-sm font-black uppercase tracking-[0.18em] text-white md:text-base">Onarf</span>
        </a>
        <a
          href="#kontakt"
          data-testid="ponuda1-header-cta"
          className="group flex items-center gap-2 bg-[#50C878] px-5 py-3 font-display text-[0.7rem] font-extrabold uppercase tracking-[0.12em] text-black transition-colors duration-300 hover:bg-[#3EB264] md:px-6 md:text-xs"
        >
          Želim garanciju
          <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative flex min-h-[100svh] w-full items-center overflow-hidden bg-[#050505]">
      <div className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Novi pacijenti za vašu kliniku" fetchpriority="high" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/74" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/45 to-[#050505]/65" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1200px] px-5 pt-28 md:px-10">
        <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="mb-7 flex items-center gap-3">
          <img src={LOGO_DIAMOND} alt="" className="h-4 w-4 object-contain" />
          <span className="eyebrow">Meta oglasi za klinike · Garancija</span>
        </motion.div>

        {/* Headline IS the guarantee — first thing the visitor sees */}
        <h1 className="max-w-4xl font-display text-[clamp(2.15rem,11vw,3.25rem)] font-black uppercase leading-[0.9] tracking-tighter text-white sm:text-7xl sm:leading-[0.88] lg:text-[6.5rem]">
          {HEADLINE.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span variants={lineReveal} initial="hidden" animate="show" custom={i} className="block">
                {line === "PACIJENATA" ? <span className="text-[#50C878]">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* The guarantee spelled out */}
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-7 flex items-start gap-3 text-lg font-bold leading-snug text-white md:text-2xl"
        >
          <ShieldCheck size={26} className="mt-0.5 flex-shrink-0 text-[#50C878]" />
          <span>…ili vam vraćamo <span className="text-[#50C878]">sav ulog</span>. Bez sitnih slova.</span>
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.72 }}
          className="mt-5 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
        >
          Vodimo Meta oglase za vašu kliniku i garantiramo 10 novih pacijenata u 30 dana.
          Ako ne isporučimo — vraćamo vam cijeli ulog. Rizik je na nama.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }} className="mt-9">
          <a
            href="#kontakt"
            data-testid="ponuda1-hero-cta"
            className="group inline-flex items-center justify-center gap-3 bg-[#50C878] px-9 py-5 font-display text-sm font-extrabold uppercase tracking-widest text-black transition-colors duration-300 hover:bg-[#3EB264]"
          >
            Osigurajte svojih 10 pacijenata
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <p className="mt-4 text-sm text-white/55">Ispunite formu i javljamo se unutar 24 sata.</p>
        </motion.div>
      </div>
    </section>
  );
}

export default function Ponuda1Page() {
  useEffect(() => {
    // Nakon kratkog prikaza heroja, glatko odvedi posjetitelja do forme.
    const id = setTimeout(() => {
      const target =
        document.querySelector('[data-testid="contact-form"]') || document.querySelector("#kontakt");
      if (!target) return;
      if (window.__lenis) {
        window.__lenis.scrollTo(target, { offset: -100, duration: 1.6 });
      } else {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 1100);
    return () => clearTimeout(id);
  }, []);

  return (
    <>
      <LandingHeader />
      <main>
        <Hero />

        {/* Guarantee band */}
        <section className="border-y border-[#50C878]/30 bg-[#0a0a0a] py-12 md:py-16">
          <div className="mx-auto flex max-w-[1200px] flex-col items-start gap-6 px-5 md:flex-row md:items-center md:justify-between md:px-10">
            <div className="flex items-center gap-4">
              <ShieldCheck size={40} className="flex-shrink-0 text-[#50C878]" />
              <p className="font-display text-2xl font-black uppercase leading-none tracking-tight text-white md:text-4xl">
                10 pacijenata / 30 dana<span className="text-[#50C878]"> — ili novac natrag</span>
              </p>
            </div>
            <a
              href="#kontakt"
              data-testid="ponuda1-band-cta"
              className="font-display text-xs font-extrabold uppercase tracking-widest text-[#50C878] underline underline-offset-4 hover:text-white"
            >
              Želim garanciju →
            </a>
          </div>
        </section>

        {/* Justification for handing over contact info */}
        <section className="bg-[#050505] py-10">
          <div className="mx-auto max-w-[1200px] px-5 md:px-10">
            <p className="flex items-start gap-4 text-base leading-relaxed text-white/75 md:text-lg">
              <img src={LOGO_DIAMOND} alt="" className="mt-1.5 h-4 w-4 object-contain" />
              U kratkom pozivu pokazujemo točno kako dolazimo do 10 novih pacijenata i zašto možemo stati iza garancije — bez obveze.
            </p>
          </div>
        </section>

        {/* Objection handling */}
        <section className="bg-[#050505] pb-16 md:pb-24">
          <div className="mx-auto max-w-[1200px] px-5 md:px-10">
            <div className="grid grid-cols-1 gap-px md:grid-cols-3">
              {OBJECTIONS.map((o, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex flex-col gap-4 py-6 md:px-8"
                  data-testid={`ponuda1-objection-${i}`}
                >
                  <h3 className="font-display text-lg font-bold leading-tight tracking-tight text-white">{o.worry}</h3>
                  <p className="flex items-start gap-3 text-sm leading-relaxed text-white/60">
                    <img src={LOGO_DIAMOND} alt="" className="diamond-bullet mt-1" />
                    <span>{o.answer}</span>
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Social proof — real results only */}
        <section className="border-y border-[#222] bg-[#0a0a0a] py-16 md:py-20">
          <div className="mx-auto max-w-[1200px] px-5 md:px-10">
            <div className="mb-10 flex items-center gap-3">
              <img src={LOGO_DIAMOND} alt="" className="diamond-bullet" />
              <span className="eyebrow">Stvarni rezultati · 5.000+ upita generirano</span>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {PROOF.map((p, i) => (
                <div key={i} className="border border-[#222] bg-[#050505] p-6" data-testid={`ponuda1-proof-${i}`}>
                  <div className="font-display text-xl font-black tracking-tight text-[#50C878]">{p.metric}</div>
                  <h3 className="mt-3 font-display text-sm font-bold uppercase tracking-wide text-white">{p.klinika}</h3>
                  <span className="text-xs text-white/40">{p.grad}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form — untouched shared component */}
        <ContactCTA />

        {/* GDPR consent line + privacy policy link */}
        <section className="bg-[#050505] pb-16">
          <div className="mx-auto max-w-[1200px] px-5 md:px-10">
            <p className="flex items-start gap-3 text-xs leading-relaxed text-white/45" data-testid="ponuda1-gdpr-consent">
              <ShieldCheck size={16} className="mt-0.5 flex-shrink-0 text-[#50C878]" />
              <span>
                Ispunjavanjem i slanjem obrasca dajete privolu za obradu vaših osobnih podataka u svrhu
                kontakta i ponude usluga. Podatke ne dijelimo s trećim stranama u marketinške svrhe. Više u{" "}
                <Link to="/privatnost" data-testid="ponuda1-privacy-link" className="text-[#50C878] underline underline-offset-2 hover:text-white">
                  Politici privatnosti
                </Link>
                .
              </span>
            </p>
          </div>
        </section>
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-[#222] bg-[#050505] py-8">
        <div className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-4 px-5 md:flex-row md:px-10">
          <div className="flex items-center gap-3">
            <img src={LOGO_DIAMOND} alt="Onarf Marketing" className="h-6 w-6 object-contain" />
            <span className="font-display text-xs font-black uppercase tracking-[0.18em] text-white">Onarf Marketing</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-white/40">
            <Link to="/privatnost" className="transition-colors hover:text-white">Politika privatnosti</Link>
            <span>© {new Date().getFullYear()} Onarf Marketing</span>
          </div>
        </div>
        <div className="mx-auto mt-6 max-w-[1200px] border-t border-[#161616] px-5 pt-6 md:px-10" data-testid="ponuda1-legal-info">
          <p className="text-[0.7rem] leading-relaxed text-white/30">
            CLEAN-SHINE, obrt za usluge, vl. Ante Budimir · Ulica Franjevačke klasične gimnazije 6 A, Sinj · OIB: 31484132617
          </p>
        </div>
      </footer>
    </>
  );
}
